/**
=========================================================
* Soft UI Dashboard PRO React - v3.0.0
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Soft UI Dashboard PRO React components
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";

const SuiBadgeDotLevelColor = forwardRef(
  ({ variant, color, size, badgeContent, font, ...rest }, ref) => {
    let finalSize;
    let fontSize;
    let padding;

    if (size === "sm") {
      finalSize = "0.5rem";
      fontSize = "caption";
      padding = "0.45em 0.775em";
    } else if (size === "lg") {
      finalSize = "0.625rem";
      fontSize = "body2";
      padding = "0.85em 1.375em";
    } else if (size === "md") {
      finalSize = "0.5rem";
      fontSize = "button";
      padding = "0.65em 1em";
    } else {
      finalSize = "0.375rem";
      fontSize = "caption";
      padding = "0.45em 0.775em";
    }

    const validColors = [
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "light",
      "dark",
      "LEVEL_1",
      "LEVEL_1+",
      "LEVEL_2",
      "LEVEL_2+",
      "LEVEL_3",
      "LEVEL_3+",
      "LEVEL_4",
      "LEVEL_4+",
      "LEVEL_5",
      "LEVEL_5+",
      "LEVEL_6",
      "LEVEL_7",
      "LEVEL_8",
      "LEVEL_9",
      "LEVEL_10",
    ];

    const validColorIndex = validColors.findIndex((el) => el === color);

    return (
      <SuiBox ref={ref} display="flex" alignItems="center" p={padding} {...rest}>
        <SuiBox
          component="i"
          display="inline-block"
          width={finalSize}
          height={finalSize}
          borderRadius="50%"
          bgColor={validColors[validColorIndex]}
          variant={variant}
          mr={1}
        />
        <SuiTypography
          variant={fontSize}
          fontWeight={font.weight ? font.weight : "regular"}
          color={font.color ? font.color : "dark"}
          sx={{ lineHeight: 0 }}
        >
          {badgeContent}
        </SuiTypography>
      </SuiBox>
    );
  }
);

// Setting default values for the props of SuiBadge
SuiBadgeDotLevelColor.defaultProps = {
  variant: "contained",
  color: "light",
  size: "xs",
  font: {},
};

// Typechecking props of the SuiBadge
SuiBadgeDotLevelColor.propTypes = {
  variant: PropTypes.oneOf(["contained", "gradient"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
    "LEVEL_1",
    "LEVEL_1+",
    "LEVEL_2",
    "LEVEL_2+",
    "LEVEL_3",
    "LEVEL_3+",
    "LEVEL_4",
    "LEVEL_4+",
    "LEVEL_5",
    "LEVEL_5+",
    "LEVEL_6",
    "LEVEL_7",
    "LEVEL_8",
    "LEVEL_9",
    "LEVEL_10",
  ]),
  size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
  badgeContent: PropTypes.string.isRequired,
  font: PropTypes.shape({
    color: PropTypes.string,
    weight: PropTypes.string,
  }),
};

export default SuiBadgeDotLevelColor;
